import React, { useCallback, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { LiaFileMedicalAltSolid } from 'react-icons/lia';
import { PiNotePencil } from 'react-icons/pi';
import ClaimBillsTable from './ClaimBillsTable';
import { ClaimWithBills, BillAction } from './ClaimDetailsTypes';
import { BillVerdictStatus } from '../../types';
import { MDTypography } from '../../components/MDTypography';
import PDFViewer from '../../components/PDFViewer/PDFViewer';
import { ContextPanel } from '../../components/ContextPanel';
import { getSignedUrl } from 'services/storageService';
import { logger } from 'services/logger';

const useStyles = makeStyles((theme) => ({
  expandedContent: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(1, 0),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  billsSummary: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    width: 'fit-content',
    maxWidth: '400px',
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    minWidth: '200px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  summaryValue: {
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  link: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

interface ClaimExpandableContentProps {
  hipaaWaiverFileUrls?: string[];
  medicalRecordFileUrls?: { fileName: string; docUrl: string }[];
  hipaaWaiverRequest?: {
    status: 'New' | 'Processing' | 'Completed';
    requestDocUrl?: string;
    signedDocUrl?: string;
    createdAt: string;
    updatedAt: string;
    uploadBy: string;
  };
  claim: ClaimWithBills;
  onBillAction: (billId: number, action: BillAction) => void;
  onBillVerdict: (
    billId: number,
    verdict: BillVerdictStatus,
    amount: number,
    reason: string
  ) => void;
  loading?: boolean;
}

export const ClaimExpandableContent: React.FC<ClaimExpandableContentProps> = ({
  claim,
  onBillAction,
  onBillVerdict,
  loading = false,
  hipaaWaiverFileUrls,
  medicalRecordFileUrls,
  hipaaWaiverRequest,
}) => {
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [docTitle, setDocTitle] = useState('');
  const classes = useStyles();

  const handleMedicalRecordRequest = useCallback(async (docUrl: string) => {
    try {
      const presignedUrl = await getSignedUrl(docUrl, 'medical-record');
      setPdfViewerOpen(true);
      setPdfUrl(presignedUrl || '');
      setDocTitle('Medical Records');
    } catch (error) {
      const e = error as Error;
      logger.error('Error fetching medical record signed URL', { error: e });
    }
  }, []);
  const handleHipaaWaiverRequest = useCallback(async (docUrl: string) => {
    try {
      const presignedUrl = await getSignedUrl(docUrl, 'waiver');
      setPdfViewerOpen(true);
      setPdfUrl(presignedUrl || '');
      setDocTitle('Signed Hipaa Waiver');
    } catch (error) {
      const e = error as Error;
      logger.error('Error fetching signed hipaa waiver URL', { error: e });
    }
  }, []);
  const hasMedicalRecords = Boolean(
    medicalRecordFileUrls && medicalRecordFileUrls.length > 0
  );
  const hasHipaaWaiver = Boolean(
    hipaaWaiverFileUrls && hipaaWaiverFileUrls.length > 0
  );
  const hadAtLeastOneDocument = hasHipaaWaiver || hasMedicalRecords;

  return (
    <div className={classes.expandedContent}>
      <Box mt={2}>
        <ClaimBillsTable
          bills={claim.bills}
          claimId={claim.fairFareInternalClaimId}
          onBillAction={onBillAction}
          onBillVerdict={onBillVerdict}
          loading={loading}
        />
      </Box>
      {hadAtLeastOneDocument ? (
        <>
          <Box mt={2}>
            {hasMedicalRecords &&
              medicalRecordFileUrls?.map((medicalFile) => (
                <Box
                  key={medicalFile.docUrl}
                  paddingX={2}
                  marginY={3}
                  height={50}
                  style={{ backgroundColor: 'white' }}
                >
                  <Box
                    onClick={() =>
                      handleMedicalRecordRequest(medicalFile.docUrl)
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <LiaFileMedicalAltSolid
                      size={24}
                      style={{ cursor: 'pointer' }}
                    />
                    <MDTypography
                      variant="caption"
                      marginLeft={1}
                      className={classes.link}
                    >
                      Medical Records - {medicalFile.fileName}
                    </MDTypography>
                  </Box>
                </Box>
              ))}
            {hasHipaaWaiver &&
              hipaaWaiverFileUrls?.map((hipaaWaiverFileUrl) => (
                <Box
                  key={hipaaWaiverFileUrl}
                  paddingX={2}
                  height={50}
                  style={{ backgroundColor: 'white' }}
                >
                  <Box
                    onClick={() => handleHipaaWaiverRequest(hipaaWaiverFileUrl)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <PiNotePencil size={24} style={{ cursor: 'pointer' }} />
                    <MDTypography
                      variant="caption"
                      marginLeft={1}
                      className={classes.link}
                    >
                      Signed Hipaa Waiver
                    </MDTypography>
                  </Box>
                </Box>
              ))}
          </Box>
          <ContextPanel
            open={pdfViewerOpen}
            onClose={() => setPdfViewerOpen(false)}
            side="right"
            size="fullsize"
            title={docTitle}
          >
            <PDFViewer pdfUrl={pdfUrl} />
          </ContextPanel>
        </>
      ) : (
        <>
          {(!hipaaWaiverRequest ||
            hipaaWaiverRequest.status !== 'Completed') && (
            <MDTypography variant="caption" color="info">
              {`Additional documents may be available once the HIPAA waiver is downloaded and signed. current status: ${
                hipaaWaiverRequest?.status === 'Processing'
                  ? 'Document is signed and sent to the provider'
                  : 'Not Requested'
              }`}
            </MDTypography>
          )}
        </>
      )}
    </div>
  );
};

export default ClaimExpandableContent;
